import { Select } from "@react-three/drei";
import GLBObject from "./GLBObject";
import { useModelStore, usePlacedModelsStore, useUserInterfaceStore } from "../../store";
import { Suspense, useCallback, useState, useEffect, useRef } from "react";
import FallbackGLB from "./FallBackGLB";

// How many models to load per wave and delay between waves.
// In the editor, models load one-at-a-time as the user places them.
// This staggering emulates that pattern so S3 isn't hit with 20+ concurrent downloads.
const BATCH_SIZE = 3;
const BATCH_DELAY_MS = 400;

export default function GLBObjectList() {
  // Get models directly - React.memo on GLBObject prevents unnecessary re-renders
  const models = usePlacedModelsStore((s) => s.placedModels);
  const playerMode = useUserInterfaceStore((s) => s.playerMode);
  const isEditing = playerMode !== "player";

  // In editor mode, show all models immediately (user adds them one at a time).
  // In player mode, stagger rendering to avoid concurrent S3 downloads.
  const [visibleCount, setVisibleCount] = useState(isEditing ? models.length : BATCH_SIZE);
  const prevModelsLenRef = useRef(models.length);

  useEffect(() => {
    // In editor mode, always show all models immediately
    if (isEditing) {
      setVisibleCount(models.length);
      prevModelsLenRef.current = models.length;
      return;
    }

    // If models list changed (room loaded), reset progressive loading
    if (models.length !== prevModelsLenRef.current) {
      prevModelsLenRef.current = models.length;
      setVisibleCount(BATCH_SIZE);
    }
  }, [models.length, isEditing]);

  useEffect(() => {
    if (isEditing || visibleCount >= models.length) return;
    const timer = setTimeout(() => {
      setVisibleCount(prev => Math.min(prev + BATCH_SIZE, models.length));
    }, BATCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [visibleCount, models.length, isEditing]);

  // Exclude procedural avatar models — rendered by PlacedAvatars, not GLBObject
  const glbModels = models.filter((m) => m.type !== "plexian_avatar");
  const visibleModels = isEditing ? glbModels : glbModels.slice(0, visibleCount);

  const setSelectedUUIDs = useModelStore((s) => s.setSelectedUUIDs);
  const setSelectedMeshes = useModelStore((s) => s.setSelectedMeshes);
  const isTransforming = useUserInterfaceStore((s) => s.isTransforming);

  const handleSelect = useCallback(
    (ev) => {
      if (isTransforming) return;

      const selectedGroups = new Set();
      const selectedMeshes = [];

      for (const mesh of ev) {
        let parent = mesh;

        // Traverse up the parent chain until we find the group root
        while (parent && !parent.userData?.isGroupRoot) {
          parent = parent.parent;
        }

        if (parent && parent.userData?.isGroupRoot) {
          selectedGroups.add(parent);
          selectedMeshes.push(parent);
        }
      }
      const selected = Array.from(selectedGroups);
      const uuids = selected.map((obj) => obj.uuid);

      setSelectedMeshes(selected);
      setSelectedUUIDs(uuids);
    },
    [setSelectedMeshes, setSelectedUUIDs, isTransforming]
  );

  return (
    <>
      <Select multiple box onChangePointerUp={handleSelect}>
        {visibleModels.map((model) => (
          <Suspense key={model.id} fallback={<FallbackGLB />}>
            {/* Pass model directly - don't spread it, that creates new object */}
            <GLBObject model={model} />
          </Suspense>
        ))}
      </Select>
    </>
  );
}
